/**
 * SeniorModulesPage - Per-Senior Module Management
 * 
 * NEW: Clean, senior-specific module management UI
 * 
 * Features:
 * - Fetch and display all system skills
 * - Show per-senior enabled/disabled state
 * - Group skills by category
 * - Visually distinguish core skills (non-disableable)
 * - Bulk save via PUT /api/seniors/{senior_id}/skills
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Loader2, Lock, Key, Check, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// TypeScript interfaces converted to JSDoc for JSX compatibility
/**
 * @typedef {Object} Skill
 * @property {string} id
 * @property {string} name
 * @property {string} key
 * @property {string} description
 * @property {string} icon
 * @property {'core' | 'free' | 'plus' | 'premium'} type
 * @property {'free' | 'plus' | 'premium'} pricing
 * @property {boolean} enabled_by_default
 * @property {boolean} requires_api_key
 * @property {string} [category]
 */

/**
 * @typedef {Object} SkillEntry
 * @property {Skill} skill
 * @property {boolean} enabled
 * @property {string} [enabled_at]
 * @property {string} [disabled_at]
 */

/**
 * @typedef {Object} SeniorProfile
 * @property {string} id
 * @property {string} name
 * @property {string} [photo_url]
 */

const categoryDisplayNames = {
  core: 'Kernfunktionen',
  communication: 'Kommunikation',
  safety: 'Sicherheit',
  wellness: 'Gesundheit',
  entertainment: 'Unterhaltung',
  information: 'Information',
  religion: 'Religion',
  accessibility: 'Barrierefreiheit',
};

const categoryOrder = [
  'core',
  'communication',
  'safety',
  'wellness',
  'entertainment',
  'information',
  'religion',
  'accessibility',
];

export default function SeniorModulesPage() {
  const { seniorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [senior, setSenior] = useState(null);
  const [skills, setSkills] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (seniorId) {
      loadData();
    }
  }, [seniorId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch senior profile
      const seniorRes = await axios.get(
        `${API}/api/seniors/${seniorId}`,
        { withCredentials: true }
      );
      setSenior(seniorRes.data);

      // Fetch senior skills (already merged with system skills)
      const skillsRes = await axios.get(
        `${API}/api/seniors/${seniorId}/skills`,
        { withCredentials: true }
      );

      setSkills(skillsRes.data.skills || []);
      console.log('[SeniorModules] Loaded', skillsRes.data.skills?.length, 'skills');
    } catch (error) {
      console.error('[SeniorModules] Load error:', error);
      toast.error('Fehler beim Laden der Module');
    } finally {
      setLoading(false);
    }
  };

  const toggleSkill = (skillKey: string) => {
    setSkills((prev) =>
      prev.map((entry) => {
        if (entry.skill.key === skillKey || entry.skill.id === skillKey) {
          // Prevent toggling core skills
          if (entry.skill.type === 'core') {
            toast.error('Kern-Module können nicht deaktiviert werden');
            return entry;
          }
          return { ...entry, enabled: !entry.enabled };
        }
        return entry;
      })
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!seniorId) return;

    try {
      setSaving(true);

      // Prepare payload
      const payload = {
        skills: skills.map((entry) => ({
          skill_key: entry.skill.key || entry.skill.id,
          enabled: entry.enabled,
        })),
      };

      // Send bulk update
      const response = await axios.put(
        `${API}/api/seniors/${seniorId}/skills`,
        payload,
        { withCredentials: true }
      );

      toast.success('Module erfolgreich gespeichert');
      setHasChanges(false);

      // Update local state with server response
      if (response.data.skills) {
        setSkills(response.data.skills);
      }
    } catch (error) {
      console.error('[SeniorModules] Save error:', error);
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Fehler beim Speichern: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  // Group skills by category
  const groupedSkills = {};
  skills.forEach((entry) => {
    const category = entry.skill.category || 'other';
    if (!groupedSkills[category]) {
      groupedSkills[category] = [];
    }
    groupedSkills[category].push(entry);
  });

  // Sort categories
  const sortedCategories = Object.keys(groupedSkills).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const renderSkillCard = (entry) => {
    const { skill, enabled } = entry;
    const isCore = skill.type === 'core';
    const skillKey = skill.key || skill.id;

    return (
      <div
        key={skill.id}
        className={`bg-white rounded-lg shadow border-2 p-4 transition-all ${
          enabled ? 'border-violet-400' : 'border-gray-200'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3">
            <span className="text-3xl">{skill.icon}</span>
            <div className="flex-1">
              <h3 className="text-base font-bold text-gray-900">{skill.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{skill.description}</p>
            </div>
          </div>

          {/* Toggle */}
          {isCore ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full shrink-0">
              <Lock className="w-4 h-4 text-gray-600" />
              <span className="text-xs font-medium text-gray-600">Kern-Modul</span>
            </div>
          ) : (
            <button
              onClick={() => toggleSkill(skillKey)}
              disabled={saving}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors shrink-0 ${
                enabled ? 'bg-violet-500' : 'bg-gray-300'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={`Toggle ${skill.name}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {/* Pricing Badge */}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              skill.pricing === 'free'
                ? 'bg-green-100 text-green-700'
                : skill.pricing === 'plus'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-purple-100 text-purple-700'
            }`}
          >
            {skill.pricing === 'free' ? 'Kostenlos' : skill.pricing.toUpperCase()}
          </span>

          {/* API Key Badge */}
          {skill.requires_api_key && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
              <Key className="w-3 h-3" />
              API-Key erforderlich
            </span>
          )}
        </div>

        {/* Status */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            {enabled ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">Aktiviert</span>
              </>
            ) : (
              <>
                <X className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-400">Deaktiviert</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryGroup = (category, entries) => {
    const displayName = categoryDisplayNames[category] || category;

    return (
      <div key={category} className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{displayName}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {entries.map(renderSkillCard)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-violet-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Module werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/family/seniors/${seniorId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Module verwalten
              </h1>
              {senior && (
                <p className="text-lg text-gray-600">
                  Konfiguration für <strong>{senior.name}</strong>
                </p>
              )}
            </div>

            {/* Save Button */}
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Speichert...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Änderungen speichern</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex gap-3">
            <div className="text-blue-600 text-xl">ℹ️</div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Hinweis zu Modulen
              </h3>
              <p className="text-sm text-blue-800">
                <strong>Kern-Module</strong> (Erinnerungen, Notfall, Videoanrufe) sind immer
                aktiviert und können nicht deaktiviert werden. Andere Module können Sie je
                nach Bedarf ein- oder ausschalten.
              </p>
            </div>
          </div>
        </div>

        {/* Skill Groups */}
        {sortedCategories.map((category) =>
          renderCategoryGroup(category, groupedSkills[category])
        )}

        {/* Empty State */}
        {skills.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Keine Module verfügbar</p>
          </div>
        )}

        {/* Fixed Save Button (Mobile) */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6 md:hidden">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-4 bg-violet-600 text-white rounded-full font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Speichern</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
